import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  ArrowLeft, Download, Book, Search, ChevronRight, ChevronDown, Shield, 
  FileCheck, Globe, DollarSign, Layers, Terminal, Users, Upload, Settings,
  Scan, Lock, Target, AlertTriangle, CheckCircle2, Zap, Code2, GitBranch,
  Building2, Crown, BarChart3, Clock, FileText, Play, Eye, RefreshCw
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const UserGuidePage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedSections, setExpandedSections] = useState({
    "getting-started": true,
    "phase1": false,
    "phase2": false,
    "phase3": false,
    "phase4": false,
    "upload-scan": false,
    "admin": false,
    "templates": false,
    "teams": false,
    "use-cases": false,
    "self-healing": false
  });

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const downloadGuide = () => {
    window.open(`${API}/guidebook-html-download`, '_blank');
  };

  // Guide sections data
  const guideSections = [
    {
      id: "getting-started",
      icon: <Play className="w-5 h-5" />,
      title: "Getting Started",
      description: "Quick start guide to create your first audit-ready architecture",
      content: (
        <div className="space-y-6">
          <div className="bg-zinc-800/30 rounded-lg p-4 border border-zinc-700/50">
            <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[#00f0ff]/20 text-[#00f0ff] flex items-center justify-center text-xs font-bold">1</span>
              Create an Account
            </h4>
            <p className="text-zinc-400 text-sm">
              Click "Sign In" on the landing page and select "Create Account". Enter your email, username, and password.
            </p>
          </div>

          <div className="bg-zinc-800/30 rounded-lg p-4 border border-zinc-700/50">
            <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[#00f0ff]/20 text-[#00f0ff] flex items-center justify-center text-xs font-bold">2</span>
              Start a New Project
            </h4>
            <p className="text-zinc-400 text-sm mb-3">
              Click "Architect My Solution" or navigate to Projects → New Project. You can choose from 6 pre-built templates or start blank.
            </p>
            <div className="flex flex-wrap gap-2">
              {["3-Tier Web", "Serverless API", "Microservices K8s", "Data Pipeline", "AI/ML Platform", "IoT Platform"].map((t, i) => (
                <span key={i} className="text-xs bg-zinc-700/50 px-2 py-1 rounded text-zinc-300">{t}</span>
              ))}
            </div>
          </div>

          <div className="bg-zinc-800/30 rounded-lg p-4 border border-zinc-700/50">
            <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[#00f0ff]/20 text-[#00f0ff] flex items-center justify-center text-xs font-bold">3</span>
              Complete the 4-Phase Workflow
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { phase: "Phase 1", title: "Intake", desc: "Define requirements" },
                { phase: "Phase 2", title: "Triple-Audit", desc: "Run FinOps, Resilience, Compliance audits" },
                { phase: "Phase 3", title: "Review", desc: "View architecture & fix issues" },
                { phase: "Phase 4", title: "Export", desc: "Download certified IaC" }
              ].map((p, i) => (
                <div key={i} className="bg-zinc-900/50 p-3 rounded border border-zinc-700/30">
                  <p className="text-[#00f0ff] text-xs font-mono">{p.phase}</p>
                  <p className="text-white text-sm font-medium">{p.title}</p>
                  <p className="text-zinc-500 text-xs">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )
    },
    {
      id: "phase1",
      icon: <FileCheck className="w-5 h-5" />,
      title: "Phase 1: Project Intake",
      description: "How to fill out the intake form with the right information",
      content: (
        <div className="space-y-6">
          <p className="text-zinc-400 text-sm">
            The intake form is divided into 5 tabs. Each tab collects specific information about your project requirements.
          </p>

          {/* Tab 1: Basic Info */}
          <div className="bg-zinc-800/30 rounded-lg p-4 border border-zinc-700/50">
            <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded bg-purple-500/20 text-purple-400 flex items-center justify-center text-xs">1</span>
              TAB 1: Basic Info
            </h4>
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-zinc-300 font-medium mb-1">Project Name</p>
                  <p className="text-zinc-500">A descriptive name for your architecture project</p>
                  <code className="text-xs text-green-400 block mt-1">Example: "E-Commerce Platform v2"</code>
                </div>
                <div>
                  <p className="text-zinc-300 font-medium mb-1">Project Type</p>
                  <p className="text-zinc-500">Select the category that best fits your application</p>
                  <code className="text-xs text-green-400 block mt-1">Options: Web App, API, Microservices, Data Pipeline, AI/ML, IoT</code>
                </div>
                <div>
                  <p className="text-zinc-300 font-medium mb-1">Cloud Platform</p>
                  <p className="text-zinc-500">Your target cloud provider</p>
                  <code className="text-xs text-green-400 block mt-1">Options: AWS, Azure, GCP</code>
                </div>
                <div>
                  <p className="text-zinc-300 font-medium mb-1">Business Narrative</p>
                  <p className="text-zinc-500">Describe data intake, processing, and outcomes</p>
                  <code className="text-xs text-green-400 block mt-1">Intake: "REST API requests", Processing: "Order validation", Outcome: "Confirmation emails"</code>
                </div>
              </div>
            </div>
          </div>

          {/* Tab 2: Scale & Ops */}
          <div className="bg-zinc-800/30 rounded-lg p-4 border border-zinc-700/50">
            <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded bg-cyan-500/20 text-cyan-400 flex items-center justify-center text-xs">2</span>
              TAB 2: Scale & Operations
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-zinc-300 font-medium mb-1">User Volume</p>
                <p className="text-zinc-500">Expected daily active users or requests</p>
                <code className="text-xs text-green-400 block mt-1">Options: "&lt;1K DAU", "1K-10K DAU", "10K-100K DAU", "100K-1M DAU", "1M+"</code>
              </div>
              <div>
                <p className="text-zinc-300 font-medium mb-1">Traffic Patterns</p>
                <p className="text-zinc-500">How traffic varies over time</p>
                <code className="text-xs text-green-400 block mt-1">Options: "Steady", "Peak hours", "Spiky/Variable", "Seasonal"</code>
              </div>
              <div>
                <p className="text-zinc-300 font-medium mb-1">Geographic Requirements</p>
                <p className="text-zinc-500">Where your users are located</p>
                <code className="text-xs text-green-400 block mt-1">Options: "Single region", "Multi-region", "Global edge"</code>
              </div>
              <div>
                <p className="text-zinc-300 font-medium mb-1">Latency Targets</p>
                <p className="text-zinc-500">Maximum acceptable response time</p>
                <code className="text-xs text-green-400 block mt-1">Options: "&lt;50ms", "&lt;100ms", "&lt;200ms", "&lt;500ms", "&lt;1s"</code>
              </div>
            </div>
          </div>

          {/* Tab 3: Security */}
          <div className="bg-zinc-800/30 rounded-lg p-4 border border-zinc-700/50">
            <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded bg-red-500/20 text-red-400 flex items-center justify-center text-xs">3</span>
              TAB 3: Security
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-zinc-300 font-medium mb-1">Data Sensitivity</p>
                <p className="text-zinc-500">Classification of data handled</p>
                <code className="text-xs text-green-400 block mt-1">Options: "Public", "Internal", "Confidential", "Highly Confidential", "Regulated"</code>
              </div>
              <div>
                <p className="text-zinc-300 font-medium mb-1">Compliance Requirements</p>
                <p className="text-zinc-500">Regulatory frameworks to comply with</p>
                <code className="text-xs text-green-400 block mt-1">Options: HIPAA, SOC2, PCI-DSS, GDPR, FedRAMP, ISO 27001</code>
              </div>
              <div className="md:col-span-2">
                <p className="text-zinc-300 font-medium mb-1">Authentication Methods</p>
                <p className="text-zinc-500">How users will authenticate</p>
                <code className="text-xs text-green-400 block mt-1">Options: JWT, OAuth2, SSO, MFA, SAML, API Keys</code>
              </div>
            </div>
            <div className="mt-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded">
              <p className="text-yellow-400 text-xs flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                <strong>Tip:</strong> For compliance audits to pass, select "Confidential" or higher data sensitivity and enable SSO + MFA for IAM checks.
              </p>
            </div>
          </div>

          {/* Tab 4: Data & DR */}
          <div className="bg-zinc-800/30 rounded-lg p-4 border border-zinc-700/50">
            <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded bg-green-500/20 text-green-400 flex items-center justify-center text-xs">4</span>
              TAB 4: Data & Disaster Recovery
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-zinc-300 font-medium mb-1">Database Type</p>
                <p className="text-zinc-500">Primary data storage technology</p>
                <code className="text-xs text-green-400 block mt-1">Options: "SQL (PostgreSQL)", "SQL (MySQL)", "NoSQL (MongoDB)", "NoSQL (DynamoDB)", "Time-series", "Multi-model"</code>
              </div>
              <div>
                <p className="text-zinc-300 font-medium mb-1">Data Volume</p>
                <p className="text-zinc-500">Expected total data storage</p>
                <code className="text-xs text-green-400 block mt-1">Options: "1-10GB", "10-100GB", "100GB-500GB", "500GB-1TB", "1TB-10TB", "10TB+"</code>
              </div>
              <div>
                <p className="text-zinc-300 font-medium mb-1">RPO (Recovery Point Objective)</p>
                <p className="text-zinc-500">Maximum acceptable data loss</p>
                <code className="text-xs text-green-400 block mt-1">Options: "0 (real-time)", "15 minutes", "1 hour", "4 hours", "24 hours"</code>
              </div>
              <div>
                <p className="text-zinc-300 font-medium mb-1">RTO (Recovery Time Objective)</p>
                <p className="text-zinc-500">Maximum acceptable downtime</p>
                <code className="text-xs text-green-400 block mt-1">Options: "15 minutes", "1 hour", "4 hours", "8 hours", "24 hours"</code>
              </div>
            </div>
          </div>

          {/* Tab 5: Budget & Tech */}
          <div className="bg-zinc-800/30 rounded-lg p-4 border border-zinc-700/50">
            <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded bg-yellow-500/20 text-yellow-400 flex items-center justify-center text-xs">5</span>
              TAB 5: Budget & Tech Stack
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-zinc-300 font-medium mb-1">Budget Priority</p>
                <p className="text-zinc-500">Optimization strategy</p>
                <code className="text-xs text-green-400 block mt-1">Options: "Cost Optimized", "Balanced", "Performance Optimized"</code>
              </div>
              <div>
                <p className="text-zinc-300 font-medium mb-1">Monthly Budget Range</p>
                <p className="text-zinc-500">Expected monthly cloud spend</p>
                <code className="text-xs text-green-400 block mt-1">Options: "&lt;$1K", "$1K-$5K", "$5K-$20K", "$20K-$50K", "$50K-$100K", "$100K+"</code>
              </div>
              <div>
                <p className="text-zinc-300 font-medium mb-1">Compute Preference</p>
                <p className="text-zinc-500">Preferred compute model</p>
                <code className="text-xs text-green-400 block mt-1">Options: "Serverless", "Containers", "Kubernetes", "VMs", "Hybrid"</code>
              </div>
              <div>
                <p className="text-zinc-300 font-medium mb-1">Language/Runtime</p>
                <p className="text-zinc-500">Primary technology stack</p>
                <code className="text-xs text-green-400 block mt-1">Options: "Node.js", "Python", "Java", "Go", ".NET", "Ruby"</code>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "phase2",
      icon: <Shield className="w-5 h-5" />,
      title: "Phase 2: Triple-Audit Suite",
      description: "Understanding the three automated audits and their findings",
      content: (
        <div className="space-y-6">
          <p className="text-zinc-400 text-sm">
            Phase 2 runs three comprehensive audits on your architecture requirements. Each audit validates different aspects of your design.
          </p>

          {/* FinOps Audit */}
          <div className="bg-zinc-800/30 rounded-lg p-4 border border-green-500/20">
            <h4 className="text-green-400 font-semibold mb-3 flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              FinOps Cost Auditor
            </h4>
            <p className="text-zinc-400 text-sm mb-3">
              Analyzes your infrastructure choices against your budget and provides cost optimization recommendations.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-zinc-900/50 p-3 rounded">
                <p className="text-white text-sm font-medium">What It Checks</p>
                <ul className="text-zinc-500 text-xs mt-2 space-y-1">
                  <li>• Compute cost estimation</li>
                  <li>• Database sizing costs</li>
                  <li>• DR overhead calculation</li>
                  <li>• Budget compliance status</li>
                </ul>
              </div>
              <div className="bg-zinc-900/50 p-3 rounded">
                <p className="text-white text-sm font-medium">Expected Output</p>
                <ul className="text-zinc-500 text-xs mt-2 space-y-1">
                  <li>• Monthly cost estimate</li>
                  <li>• Cost breakdown by category</li>
                  <li>• Optimization tips</li>
                  <li>• Budget status (PASS/WARNING/FAIL)</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Resilience Audit */}
          <div className="bg-zinc-800/30 rounded-lg p-4 border border-cyan-500/20">
            <h4 className="text-cyan-400 font-semibold mb-3 flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Global Resilience Audit
            </h4>
            <p className="text-zinc-400 text-sm mb-3">
              Evaluates high availability, disaster recovery, and single points of failure in your architecture.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-zinc-900/50 p-3 rounded">
                <p className="text-white text-sm font-medium">What It Checks</p>
                <ul className="text-zinc-500 text-xs mt-2 space-y-1">
                  <li>• Multi-AZ deployment</li>
                  <li>• Auto-scaling configuration</li>
                  <li>• Backup strategy (RPO/RTO)</li>
                  <li>• Single Point of Failure (SPOF)</li>
                </ul>
              </div>
              <div className="bg-zinc-900/50 p-3 rounded">
                <p className="text-white text-sm font-medium">Expected Output</p>
                <ul className="text-zinc-500 text-xs mt-2 space-y-1">
                  <li>• Resilience score (0-100)</li>
                  <li>• HA tier classification</li>
                  <li>• MTTR estimate</li>
                  <li>• SPOF list with fixes</li>
                </ul>
              </div>
            </div>
            <div className="mt-3 p-3 bg-cyan-500/10 rounded">
              <p className="text-cyan-400 text-xs">
                <strong>Pass Requirements:</strong> SLA ≥99.9% + DR strategy (Warm Standby or Active-Active) + RPO/RTO configured
              </p>
            </div>
          </div>

          {/* Compliance Audit */}
          <div className="bg-zinc-800/30 rounded-lg p-4 border border-purple-500/20">
            <h4 className="text-purple-400 font-semibold mb-3 flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Security & Compliance Gate
            </h4>
            <p className="text-zinc-400 text-sm mb-3">
              Validates your architecture against security best practices and compliance frameworks using 31 integrated tools.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-zinc-900/50 p-3 rounded">
                <p className="text-white text-sm font-medium">Security Tools Used</p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {["Checkov", "tfsec", "Trivy", "Prowler", "GitLeaks", "cfn-nag"].map((tool, i) => (
                    <span key={i} className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded">{tool}</span>
                  ))}
                </div>
              </div>
              <div className="bg-zinc-900/50 p-3 rounded">
                <p className="text-white text-sm font-medium">Compliance Frameworks</p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {["HIPAA", "SOC2", "PCI-DSS", "GDPR", "NIST 800-53", "ISO 27001"].map((fw, i) => (
                    <span key={i} className="text-xs bg-zinc-700 text-zinc-300 px-2 py-0.5 rounded">{fw}</span>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-3 space-y-2">
              <p className="text-white text-sm font-medium">Key Compliance Checks:</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                <div className="bg-zinc-900/50 p-2 rounded">
                  <p className="text-zinc-300">Encryption at Rest</p>
                  <p className="text-zinc-500">Requires: Data Sensitivity = Confidential+</p>
                </div>
                <div className="bg-zinc-900/50 p-2 rounded">
                  <p className="text-zinc-300">IAM Least Privilege</p>
                  <p className="text-zinc-500">Requires: SSO + MFA enabled</p>
                </div>
                <div className="bg-zinc-900/50 p-2 rounded">
                  <p className="text-zinc-300">Private Networking</p>
                  <p className="text-zinc-500">Requires: Data Sensitivity = Confidential+</p>
                </div>
              </div>
            </div>
          </div>

          {/* Remediation */}
          <div className="bg-zinc-800/30 rounded-lg p-4 border border-yellow-500/20">
            <h4 className="text-yellow-400 font-semibold mb-3 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Prescriptive Remediation
            </h4>
            <p className="text-zinc-400 text-sm mb-3">
              When audits find issues, the system provides specific guidance on which form fields to update.
            </p>
            <div className="bg-zinc-900/50 p-3 rounded">
              <p className="text-white text-sm mb-2">Each remediation item includes:</p>
              <ul className="text-zinc-400 text-xs space-y-1">
                <li>• <strong className="text-white">Severity</strong> - Critical, High, Medium</li>
                <li>• <strong className="text-white">Impact</strong> - What happens if not fixed</li>
                <li>• <strong className="text-white">Fix Instructions</strong> - Exactly what to change</li>
                <li>• <strong className="text-white">"Fix Now" Button</strong> - Navigates to the correct tab and field</li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "phase3",
      icon: <Eye className="w-5 h-5" />,
      title: "Phase 3: Architecture Review",
      description: "Reviewing generated architecture, diagrams, and CI/CD templates",
      content: (
        <div className="space-y-6">
          <p className="text-zinc-400 text-sm">
            Phase 3 generates a complete architecture based on your requirements and audit results. Review and refine before export.
          </p>

          <div className="bg-zinc-800/30 rounded-lg p-4 border border-zinc-700/50">
            <h4 className="text-white font-semibold mb-3">Generated Content</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-zinc-900/50 p-3 rounded">
                <p className="text-[#00f0ff] text-sm font-medium mb-2">Executive Summary</p>
                <p className="text-zinc-500 text-xs">High-level overview of the architecture, key components, and design decisions.</p>
              </div>
              <div className="bg-zinc-900/50 p-3 rounded">
                <p className="text-[#00f0ff] text-sm font-medium mb-2">Architecture Diagram</p>
                <p className="text-zinc-500 text-xs">Visual Mermaid.js diagram with component relationships. Toggle between visual and code view.</p>
              </div>
              <div className="bg-zinc-900/50 p-3 rounded">
                <p className="text-[#00f0ff] text-sm font-medium mb-2">Component Specifications</p>
                <p className="text-zinc-500 text-xs">Detailed specs for each service: compute, database, storage, networking.</p>
              </div>
              <div className="bg-zinc-900/50 p-3 rounded">
                <p className="text-[#00f0ff] text-sm font-medium mb-2">Security Architecture</p>
                <p className="text-zinc-500 text-xs">Network topology, IAM policies, encryption configuration, and access controls.</p>
              </div>
            </div>
          </div>

          <div className="bg-zinc-800/30 rounded-lg p-4 border border-zinc-700/50">
            <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
              <GitBranch className="w-4 h-4" />
              CI/CD Pipeline Templates
            </h4>
            <p className="text-zinc-400 text-sm mb-3">
              Select and customize pre-built CI/CD templates for your deployment pipeline.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {["GitHub Actions", "GitLab CI", "Jenkins", "ArgoCD"].map((platform, i) => (
                <div key={i} className="bg-zinc-900/50 p-2 rounded text-center">
                  <p className="text-zinc-300 text-xs">{platform}</p>
                </div>
              ))}
            </div>
            <p className="text-zinc-500 text-xs mt-3">
              Templates include: Docker build, Terraform deploy, Kubernetes rollout, Serverless deployment
            </p>
          </div>

          <div className="bg-zinc-800/30 rounded-lg p-4 border border-zinc-700/50">
            <h4 className="text-white font-semibold mb-3">Actions Available</h4>
            <div className="flex flex-wrap gap-2">
              <span className="text-xs bg-[#00f0ff]/10 text-[#00f0ff] px-3 py-1.5 rounded-full">Regenerate Architecture</span>
              <span className="text-xs bg-[#00f0ff]/10 text-[#00f0ff] px-3 py-1.5 rounded-full">Download SVG Diagram</span>
              <span className="text-xs bg-[#00f0ff]/10 text-[#00f0ff] px-3 py-1.5 rounded-full">Copy CI/CD Template</span>
              <span className="text-xs bg-[#00f0ff]/10 text-[#00f0ff] px-3 py-1.5 rounded-full">View Version History</span>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "phase4",
      icon: <Code2 className="w-5 h-5" />,
      title: "Phase 4: Certified Export",
      description: "Generating and downloading production-ready Infrastructure as Code",
      content: (
        <div className="space-y-6">
          <p className="text-zinc-400 text-sm">
            Phase 4 generates production-ready IaC with optional security scanning. Export formats depend on your subscription tier.
          </p>

          <div className="bg-zinc-800/30 rounded-lg p-4 border border-zinc-700/50">
            <h4 className="text-white font-semibold mb-3">IaC Tool Selection</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { name: "Terraform", desc: "Multi-cloud IaC", tier: "growth" },
                { name: "CloudFormation", desc: "AWS native", tier: "growth" },
                { name: "Bicep", desc: "Azure native", tier: "growth" },
                { name: "CDK", desc: "TypeScript/Python", tier: "enterprise" },
                { name: "Pulumi", desc: "Multi-language", tier: "enterprise" },
                { name: "Ansible", desc: "Configuration mgmt", tier: "enterprise" }
              ].map((tool, i) => (
                <div key={i} className="bg-zinc-900/50 p-3 rounded">
                  <p className="text-white text-sm font-medium">{tool.name}</p>
                  <p className="text-zinc-500 text-xs">{tool.desc}</p>
                  <span className={`text-xs px-2 py-0.5 rounded mt-2 inline-block ${
                    tool.tier === 'growth' ? 'bg-cyan-500/20 text-cyan-400' : 'bg-purple-500/20 text-purple-400'
                  }`}>
                    {tool.tier.charAt(0).toUpperCase() + tool.tier.slice(1)}+
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-zinc-800/30 rounded-lg p-4 border border-zinc-700/50">
            <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
              <Scan className="w-4 h-4 text-purple-400" />
              IaC Security Scanning (Growth+ Only)
            </h4>
            <p className="text-zinc-400 text-sm mb-3">
              Run real security scanners on your generated IaC before export.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
              {["Checkov", "tfsec", "Trivy", "GitLeaks"].map((tool, i) => (
                <div key={i} className="bg-zinc-900/50 p-2 rounded text-center">
                  <p className="text-purple-400 text-xs font-mono">{tool}</p>
                </div>
              ))}
            </div>
            <p className="text-zinc-500 text-xs">
              Scan results show severity, file location, and remediation advice for each finding.
            </p>
          </div>

          <div className="bg-zinc-800/30 rounded-lg p-4 border border-zinc-700/50">
            <h4 className="text-white font-semibold mb-3">Export Options</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between bg-zinc-900/50 p-3 rounded">
                <div>
                  <p className="text-white text-sm">Markdown Export</p>
                  <p className="text-zinc-500 text-xs">Full architecture documentation</p>
                </div>
                <span className="text-xs bg-zinc-700 text-zinc-300 px-2 py-1 rounded">All Tiers</span>
              </div>
              <div className="flex items-center justify-between bg-zinc-900/50 p-3 rounded">
                <div>
                  <p className="text-white text-sm">IaC Code Download</p>
                  <p className="text-zinc-500 text-xs">Ready-to-deploy infrastructure code</p>
                </div>
                <span className="text-xs bg-cyan-500/20 text-cyan-400 px-2 py-1 rounded">Growth+</span>
              </div>
              <div className="flex items-center justify-between bg-zinc-900/50 p-3 rounded">
                <div>
                  <p className="text-white text-sm">PDF Certificate</p>
                  <p className="text-zinc-500 text-xs">Deployment readiness certification</p>
                </div>
                <span className="text-xs bg-cyan-500/20 text-cyan-400 px-2 py-1 rounded">Growth+</span>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "upload-scan",
      icon: <Upload className="w-5 h-5" />,
      title: "Upload & Scan Your IaC",
      description: "Upload your own IaC files for security scanning",
      content: (
        <div className="space-y-6">
          <p className="text-zinc-400 text-sm">
            Registered users can upload their own Terraform, CloudFormation, or other IaC files for security analysis using enterprise-grade scanning tools.
          </p>

          {/* NEW: Standalone Scan Page Highlight */}
          <div className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 rounded-lg p-4 border border-emerald-500/30">
            <h4 className="text-emerald-400 font-semibold mb-2 flex items-center gap-2">
              <Scan className="w-4 h-4" />
              NEW: Standalone Scan Page
            </h4>
            <p className="text-zinc-400 text-sm mb-3">
              Access the IaC Security Scanner directly without creating a project!
            </p>
            <div className="flex flex-wrap gap-3">
              <div className="bg-zinc-900/50 rounded px-3 py-2">
                <p className="text-zinc-500 text-xs">Direct URL</p>
                <code className="text-[#00f0ff] text-sm font-mono">/scan</code>
              </div>
              <div className="bg-zinc-900/50 rounded px-3 py-2">
                <p className="text-zinc-500 text-xs">Navigation</p>
                <span className="text-white text-sm">Header → "Bring Your Code" button</span>
              </div>
              <div className="bg-zinc-900/50 rounded px-3 py-2">
                <p className="text-zinc-500 text-xs">Help Menu</p>
                <span className="text-white text-sm">"Scan Your IaC" option</span>
              </div>
            </div>
          </div>

          <div className="bg-zinc-800/30 rounded-lg p-4 border border-zinc-700/50">
            <h4 className="text-white font-semibold mb-3">How to Access Upload & Scan</h4>
            <div className="space-y-4">
              <div className="p-3 bg-zinc-900/50 rounded-lg border border-zinc-700/30">
                <p className="text-cyan-400 text-sm font-medium mb-2">Option 1: Standalone Scan Page (Recommended)</p>
                <ol className="space-y-2 text-sm text-zinc-400">
                  <li>1. Click "Bring Your Code" in the navigation header</li>
                  <li>2. Or go directly to <code className="text-[#00f0ff] bg-zinc-800 px-1 rounded">/scan</code></li>
                  <li>3. Sign in if not already logged in</li>
                  <li>4. Upload files and run scans immediately</li>
                </ol>
              </div>
              <div className="p-3 bg-zinc-900/50 rounded-lg border border-zinc-700/30">
                <p className="text-purple-400 text-sm font-medium mb-2">Option 2: Within Project Workflow</p>
                <ol className="space-y-2 text-sm text-zinc-400">
                  <li>1. Create or open a project</li>
                  <li>2. Navigate to Phase 4: Certified Export</li>
                  <li>3. Generate IaC code first</li>
                  <li>4. Click the "Upload & Scan" tab</li>
                </ol>
              </div>
            </div>
          </div>

          <div className="bg-zinc-800/30 rounded-lg p-4 border border-zinc-700/50">
            <h4 className="text-white font-semibold mb-3">Security Scanning Tools</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-zinc-900/50 p-3 rounded text-center">
                <p className="text-purple-400 font-mono text-sm font-semibold">Checkov</p>
                <p className="text-zinc-500 text-xs mt-1">General IaC Scanner</p>
              </div>
              <div className="bg-zinc-900/50 p-3 rounded text-center">
                <p className="text-cyan-400 font-mono text-sm font-semibold">tfsec</p>
                <p className="text-zinc-500 text-xs mt-1">Terraform Security</p>
              </div>
              <div className="bg-zinc-900/50 p-3 rounded text-center">
                <p className="text-green-400 font-mono text-sm font-semibold">Trivy</p>
                <p className="text-zinc-500 text-xs mt-1">Vulnerability Scanner</p>
              </div>
              <div className="bg-zinc-900/50 p-3 rounded text-center">
                <p className="text-yellow-400 font-mono text-sm font-semibold">GitLeaks</p>
                <p className="text-zinc-500 text-xs mt-1">Secret Detection</p>
              </div>
            </div>
          </div>

          <div className="bg-zinc-800/30 rounded-lg p-4 border border-zinc-700/50">
            <h4 className="text-white font-semibold mb-3">Supported File Types</h4>
            <div className="flex flex-wrap gap-2">
              {[".tf", ".tfvars", ".yaml", ".yml", ".json", ".template", ".bicep"].map((ext, i) => (
                <span key={i} className="text-xs bg-zinc-700/50 text-zinc-300 px-3 py-1.5 rounded-full font-mono">{ext}</span>
              ))}
            </div>
          </div>

          <div className="bg-zinc-800/30 rounded-lg p-4 border border-zinc-700/50">
            <h4 className="text-white font-semibold mb-3">Tier Limits</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-700">
                    <th className="text-left py-2 text-zinc-400 font-medium">Feature</th>
                    <th className="text-center py-2 text-zinc-400 font-medium">Architect (Free)</th>
                    <th className="text-center py-2 text-cyan-400 font-medium">Growth</th>
                    <th className="text-center py-2 text-purple-400 font-medium">Enterprise</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800">
                  <tr>
                    <td className="py-2 text-zinc-300">Scans/Day</td>
                    <td className="py-2 text-center text-zinc-400">3</td>
                    <td className="py-2 text-center text-zinc-300">20</td>
                    <td className="py-2 text-center text-zinc-300">Unlimited</td>
                  </tr>
                  <tr>
                    <td className="py-2 text-zinc-300">Files/Scan</td>
                    <td className="py-2 text-center text-zinc-400">5</td>
                    <td className="py-2 text-center text-zinc-300">20</td>
                    <td className="py-2 text-center text-zinc-300">50</td>
                  </tr>
                  <tr>
                    <td className="py-2 text-zinc-300">Max File Size</td>
                    <td className="py-2 text-center text-zinc-400">500KB</td>
                    <td className="py-2 text-center text-zinc-300">2MB</td>
                    <td className="py-2 text-center text-zinc-300">5MB</td>
                  </tr>
                  <tr>
                    <td className="py-2 text-zinc-300">Max Total Size</td>
                    <td className="py-2 text-center text-zinc-400">2MB</td>
                    <td className="py-2 text-center text-zinc-300">20MB</td>
                    <td className="py-2 text-center text-zinc-300">50MB</td>
                  </tr>
                  <tr>
                    <td className="py-2 text-zinc-300">Scan History</td>
                    <td className="py-2 text-center text-zinc-400">5 scans / 15 days</td>
                    <td className="py-2 text-center text-zinc-300">Unlimited</td>
                    <td className="py-2 text-center text-zinc-300">Unlimited</td>
                  </tr>
                  <tr>
                    <td className="py-2 text-zinc-300">Report Downloads</td>
                    <td className="py-2 text-center text-zinc-400">1 lifetime</td>
                    <td className="py-2 text-center text-zinc-300">Credit-based</td>
                    <td className="py-2 text-center text-zinc-300">Credit-based</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-zinc-800/30 rounded-lg p-4 border border-zinc-700/50">
            <h4 className="text-white font-semibold mb-3">Scan Results Include</h4>
            <ul className="space-y-2 text-sm text-zinc-400">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                <span><strong className="text-white">Severity Classification</strong> - Critical, High, Medium, Low, Info</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                <span><strong className="text-white">File Location</strong> - Exact file path and line numbers</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                <span><strong className="text-white">Resource Identification</strong> - Which resource is affected</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                <span><strong className="text-white">Remediation Advice</strong> - How to fix each issue</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                <span><strong className="text-white">Reference URLs</strong> - Links to detailed documentation</span>
              </li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: "teams",
      icon: <Building2 className="w-5 h-5" />,
      title: "Teams & Organizations",
      description: "Collaborate with team members on architecture projects",
      content: (
        <div className="space-y-6">
          <p className="text-zinc-400 text-sm">
            Create organizations to share projects and collaborate with team members.
          </p>

          <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3 mb-4">
            <p className="text-cyan-400 text-sm flex items-center gap-2">
              <Lock className="w-4 h-4" />
              <strong>Note:</strong> Sign in required to access Teams & Organizations features.
            </p>
          </div>

          <div className="bg-zinc-800/30 rounded-lg p-4 border border-zinc-700/50">
            <h4 className="text-white font-semibold mb-3">Creating an Organization</h4>
            <ol className="space-y-2 text-sm text-zinc-400">
              <li>1. Sign in to your account</li>
              <li>2. Navigate to Projects → Teams tab</li>
              <li>3. Click "Create Organization"</li>
              <li>4. Enter organization name and description</li>
              <li>5. Invite members by email</li>
            </ol>
          </div>

          <div className="bg-zinc-800/30 rounded-lg p-4 border border-zinc-700/50">
            <h4 className="text-white font-semibold mb-3">Member Roles</h4>
            <div className="space-y-3">
              {[
                { role: "Owner", desc: "Full access, can delete org, manage all members", color: "text-yellow-400" },
                { role: "Admin", desc: "Can invite/remove members, manage projects", color: "text-cyan-400" },
                { role: "Member", desc: "Can view and work on shared projects", color: "text-green-400" },
                { role: "Viewer", desc: "Read-only access to shared projects", color: "text-zinc-400" }
              ].map((r, i) => (
                <div key={i} className="flex items-start gap-3 bg-zinc-900/50 p-3 rounded">
                  <span className={`text-sm font-medium ${r.color}`}>{r.role}</span>
                  <p className="text-zinc-500 text-xs">{r.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )
    },
    {
      id: "admin",
      icon: <Crown className="w-5 h-5" />,
      title: "Admin Dashboard",
      description: "Managing users, projects, and platform analytics (Admin Only)",
      content: (
        <div className="space-y-6">
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
            <p className="text-yellow-400 text-sm flex items-center gap-2">
              <Lock className="w-4 h-4" />
              Admin access is restricted to authorized administrators only.
            </p>
          </div>

          <div className="bg-zinc-800/30 rounded-lg p-4 border border-zinc-700/50">
            <h4 className="text-white font-semibold mb-3">Accessing Admin Dashboard</h4>
            <div className="bg-zinc-900/50 p-3 rounded mb-3">
              <p className="text-zinc-400 text-sm">URL: <code className="text-[#00f0ff]">/admin/login</code></p>
              <p className="text-zinc-500 text-xs mt-1">Contact your administrator for access credentials.</p>
            </div>
          </div>

          <div className="bg-zinc-800/30 rounded-lg p-4 border border-zinc-700/50">
            <h4 className="text-white font-semibold mb-3">Dashboard Features</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-zinc-900/50 p-3 rounded">
                <p className="text-cyan-400 text-sm font-medium mb-1">Overview Tab</p>
                <ul className="text-zinc-500 text-xs space-y-1">
                  <li>• Total users, projects, audits</li>
                  <li>• Monthly recurring revenue (MRR)</li>
                  <li>• Subscription tier breakdown</li>
                  <li>• Projects by phase</li>
                </ul>
              </div>
              <div className="bg-zinc-900/50 p-3 rounded">
                <p className="text-cyan-400 text-sm font-medium mb-1">Users Tab</p>
                <ul className="text-zinc-500 text-xs space-y-1">
                  <li>• Search and filter users</li>
                  <li>• Change subscription tiers</li>
                  <li>• Enable/disable accounts</li>
                  <li>• Grant admin privileges</li>
                </ul>
              </div>
              <div className="bg-zinc-900/50 p-3 rounded">
                <p className="text-cyan-400 text-sm font-medium mb-1">Projects Tab</p>
                <ul className="text-zinc-500 text-xs space-y-1">
                  <li>• View all projects</li>
                  <li>• See project owners</li>
                  <li>• Track project phases</li>
                </ul>
              </div>
              <div className="bg-zinc-900/50 p-3 rounded">
                <p className="text-cyan-400 text-sm font-medium mb-1">Activity Tab</p>
                <ul className="text-zinc-500 text-xs space-y-1">
                  <li>• Real-time activity feed</li>
                  <li>• User registrations</li>
                  <li>• Project creations</li>
                  <li>• Audit completions</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "templates",
      icon: <Layers className="w-5 h-5" />,
      title: "Project Templates",
      description: "Pre-built architecture templates for common use cases",
      content: (
        <div className="space-y-6">
          <p className="text-zinc-400 text-sm">
            Start with a pre-configured template to speed up your architecture design. Each template pre-fills the Phase 1 form with recommended values.
          </p>

          {[
            { name: "3-Tier Web Application", desc: "E-commerce, CMS, enterprise web apps", cost: "$2K-$8K/mo", complexity: "Medium" },
            { name: "Serverless API", desc: "Microservices, webhooks, mobile backends", cost: "$500-$3K/mo", complexity: "Low" },
            { name: "Microservices on Kubernetes", desc: "Complex enterprise applications", cost: "$10K-$50K/mo", complexity: "High" },
            { name: "Data Pipeline & Analytics", desc: "ETL/ELT, data lake, BI integration", cost: "$3K-$15K/mo", complexity: "Medium" },
            { name: "AI/ML Platform", desc: "Training, serving, model monitoring", cost: "$15K-$100K+/mo", complexity: "High" },
            { name: "IoT Platform", desc: "Device management, telemetry, edge computing", cost: "$5K-$25K/mo", complexity: "High" }
          ].map((template, i) => (
            <div key={i} className="bg-zinc-800/30 rounded-lg p-4 border border-zinc-700/50">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="text-white font-semibold">{template.name}</h4>
                  <p className="text-zinc-500 text-sm">{template.desc}</p>
                </div>
                <div className="text-right">
                  <p className="text-[#00f0ff] text-sm font-mono">{template.cost}</p>
                  <p className="text-zinc-500 text-xs">{template.complexity} complexity</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )
    },
    {
      id: "use-cases",
      icon: <Target className="w-5 h-5" />,
      title: "Sample Use Cases",
      description: "Real-world examples with recommended input values",
      content: (
        <div className="space-y-6">
          <p className="text-zinc-400 text-sm">
            Below are sample configurations for common scenarios. Use these as starting points for your own architectures.
          </p>

          {/* Healthcare Platform */}
          <div className="bg-zinc-800/30 rounded-lg p-4 border border-blue-500/20">
            <h4 className="text-blue-400 font-semibold mb-3">Healthcare Patient Portal (HIPAA)</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-xs">
              <div className="space-y-2">
                <p className="text-zinc-300 font-medium">Tab 1: Basic Info</p>
                <ul className="text-zinc-500 space-y-1">
                  <li>• Type: Web Application</li>
                  <li>• Cloud: AWS</li>
                  <li>• Intake: Patient records, appointments</li>
                </ul>
              </div>
              <div className="space-y-2">
                <p className="text-zinc-300 font-medium">Tab 2: Scale & Ops</p>
                <ul className="text-zinc-500 space-y-1">
                  <li>• Users: 10K-100K DAU</li>
                  <li>• Traffic: Steady with peak hours</li>
                  <li>• Latency: &lt;200ms</li>
                  <li>• SLA: 99.9%</li>
                </ul>
              </div>
              <div className="space-y-2">
                <p className="text-zinc-300 font-medium">Tab 3: Security</p>
                <ul className="text-zinc-500 space-y-1">
                  <li>• Sensitivity: Regulated</li>
                  <li>• Compliance: HIPAA, SOC2</li>
                  <li>• Auth: SSO, MFA, OAuth2</li>
                </ul>
              </div>
              <div className="space-y-2">
                <p className="text-zinc-300 font-medium">Tab 4: Data & DR</p>
                <ul className="text-zinc-500 space-y-1">
                  <li>• Database: SQL (PostgreSQL)</li>
                  <li>• RPO: 15 minutes</li>
                  <li>• RTO: 1 hour</li>
                  <li>• DR: Active-Passive</li>
                </ul>
              </div>
              <div className="space-y-2">
                <p className="text-zinc-300 font-medium">Tab 5: Budget & Tech</p>
                <ul className="text-zinc-500 space-y-1">
                  <li>• Priority: Balanced</li>
                  <li>• Budget: $5K-$20K/mo</li>
                  <li>• Compute: Containers</li>
                  <li>• Language: Python, Node.js</li>
                </ul>
              </div>
            </div>
            <div className="mt-3 p-2 bg-blue-500/10 rounded">
              <p className="text-blue-400 text-xs">
                <strong>Expected:</strong> High compliance score, encryption enforced, private networking required
              </p>
            </div>
          </div>

          {/* E-Commerce Platform */}
          <div className="bg-zinc-800/30 rounded-lg p-4 border border-green-500/20">
            <h4 className="text-green-400 font-semibold mb-3">E-Commerce Platform (PCI-DSS)</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-xs">
              <div className="space-y-2">
                <p className="text-zinc-300 font-medium">Tab 1: Basic Info</p>
                <ul className="text-zinc-500 space-y-1">
                  <li>• Type: Web Application</li>
                  <li>• Cloud: AWS</li>
                  <li>• Intake: Orders, payments, inventory</li>
                </ul>
              </div>
              <div className="space-y-2">
                <p className="text-zinc-300 font-medium">Tab 2: Scale & Ops</p>
                <ul className="text-zinc-500 space-y-1">
                  <li>• Users: 100K-1M DAU</li>
                  <li>• Traffic: Spiky (flash sales)</li>
                  <li>• Latency: &lt;100ms</li>
                  <li>• SLA: 99.95%</li>
                </ul>
              </div>
              <div className="space-y-2">
                <p className="text-zinc-300 font-medium">Tab 3: Security</p>
                <ul className="text-zinc-500 space-y-1">
                  <li>• Sensitivity: Highly Confidential</li>
                  <li>• Compliance: PCI-DSS, GDPR</li>
                  <li>• Auth: JWT, MFA</li>
                </ul>
              </div>
              <div className="space-y-2">
                <p className="text-zinc-300 font-medium">Tab 4: Data & DR</p>
                <ul className="text-zinc-500 space-y-1">
                  <li>• Database: SQL (PostgreSQL)</li>
                  <li>• RPO: 15 minutes</li>
                  <li>• RTO: 1 hour</li>
                  <li>• DR: Active-Passive</li>
                </ul>
              </div>
              <div className="space-y-2">
                <p className="text-zinc-300 font-medium">Tab 5: Budget & Tech</p>
                <ul className="text-zinc-500 space-y-1">
                  <li>• Priority: Performance Optimized</li>
                  <li>• Budget: $20K-$50K/mo</li>
                  <li>• Compute: Kubernetes</li>
                  <li>• Language: Node.js, Go</li>
                </ul>
              </div>
            </div>
            <div className="mt-3 p-2 bg-green-500/10 rounded">
              <p className="text-green-400 text-xs">
                <strong>Expected:</strong> High resilience score, auto-scaling enabled, CDN caching for performance
              </p>
            </div>
          </div>

          {/* FinTech API */}
          <div className="bg-zinc-800/30 rounded-lg p-4 border border-yellow-500/20">
            <h4 className="text-yellow-400 font-semibold mb-3">FinTech Payment API (SOC2)</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-xs">
              <div className="space-y-2">
                <p className="text-zinc-300 font-medium">Tab 1: Basic Info</p>
                <ul className="text-zinc-500 space-y-1">
                  <li>• Type: API Service</li>
                  <li>• Cloud: GCP</li>
                  <li>• Intake: Payment requests, webhooks</li>
                </ul>
              </div>
              <div className="space-y-2">
                <p className="text-zinc-300 font-medium">Tab 2: Scale & Ops</p>
                <ul className="text-zinc-500 space-y-1">
                  <li>• Users: 1M+ requests/day</li>
                  <li>• Traffic: Variable, event-driven</li>
                  <li>• Latency: &lt;50ms</li>
                  <li>• SLA: 99.99%</li>
                </ul>
              </div>
              <div className="space-y-2">
                <p className="text-zinc-300 font-medium">Tab 3: Security</p>
                <ul className="text-zinc-500 space-y-1">
                  <li>• Sensitivity: Regulated</li>
                  <li>• Compliance: SOC2, PCI-DSS</li>
                  <li>• Auth: API Keys, OAuth2, MFA</li>
                </ul>
              </div>
              <div className="space-y-2">
                <p className="text-zinc-300 font-medium">Tab 4: Data & DR</p>
                <ul className="text-zinc-500 space-y-1">
                  <li>• Database: Multi-model</li>
                  <li>• RPO: 0 (real-time)</li>
                  <li>• RTO: 15 minutes</li>
                  <li>• DR: Active-Active</li>
                </ul>
              </div>
              <div className="space-y-2">
                <p className="text-zinc-300 font-medium">Tab 5: Budget & Tech</p>
                <ul className="text-zinc-500 space-y-1">
                  <li>• Priority: Performance Optimized</li>
                  <li>• Budget: $50K-$100K/mo</li>
                  <li>• Compute: Kubernetes</li>
                  <li>• Language: Go, Java</li>
                </ul>
              </div>
            </div>
            <div className="mt-3 p-2 bg-yellow-500/10 rounded">
              <p className="text-yellow-400 text-xs">
                <strong>Expected:</strong> Maximum resilience, zero-downtime deployments, real-time replication
              </p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "self-healing",
      icon: <RefreshCw className="w-5 h-5" />,
      title: "Self-Healing Engine: How Auto-Remediation Works",
      description: "Deep dive into how ZeroZone automatically fixes issues before presenting code",
      content: (
        <div className="space-y-6">
          <p className="text-zinc-400 text-sm">
            ZeroZone's Self-Healing Engine is the core innovation that transforms a simple architecture generator into an audit-ready certification platform. Here's exactly how it works:
          </p>

          {/* Overview Diagram */}
          <div className="bg-gradient-to-r from-[#00f0ff]/10 to-purple-500/10 rounded-lg p-6 border border-[#00f0ff]/30">
            <h4 className="text-white font-semibold mb-4 text-center">Auto-Remediation Pipeline</h4>
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-center">
              {[
                { step: "1", title: "Input Analysis", desc: "Parse requirements", color: "cyan" },
                { step: "2", title: "Triple Audit", desc: "FinOps + Resilience + Compliance", color: "purple" },
                { step: "3", title: "Issue Detection", desc: "Identify gaps & violations", color: "yellow" },
                { step: "4", title: "Auto-Fix Logic", desc: "Apply remediation rules", color: "green" },
                { step: "5", title: "Clean Output", desc: "Certified IaC ready", color: "cyan" }
              ].map((item, i) => (
                <div key={i} className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full bg-${item.color}-500/20 border border-${item.color}-500/50 flex items-center justify-center text-${item.color}-400 font-bold text-sm mb-2`}>
                    {item.step}
                  </div>
                  <p className="text-white text-sm font-medium">{item.title}</p>
                  <p className="text-zinc-500 text-xs">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Step 1: Pre-Generation Audit */}
          <div className="bg-zinc-800/30 rounded-lg p-4 border border-cyan-500/20">
            <h4 className="text-cyan-400 font-semibold mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded bg-cyan-500/20 flex items-center justify-center text-xs font-bold">1</span>
              Pre-Generation Audit (Before Architecture)
            </h4>
            <p className="text-zinc-400 text-sm mb-3">
              Before generating ANY architecture, the engine analyzes your Phase 1 inputs against security benchmarks.
            </p>
            <div className="bg-zinc-900/50 rounded p-4 font-mono text-xs overflow-x-auto">
              <p className="text-zinc-500 mb-2"># What happens when you submit Phase 1:</p>
              <p className="text-green-400">1. run_finops_audit(budget, compute, storage...)</p>
              <p className="text-green-400">2. run_resilience_audit(sla, dr_strategy, rpo, rto...)</p>
              <p className="text-green-400">3. run_compliance_audit(data_sensitivity, auth_methods...)</p>
              <p className="text-yellow-400 mt-2"># Output: Audit results with issues list</p>
              <p className="text-zinc-500 mt-2"># Example issue detected:</p>
              <p className="text-red-400">{`{ "id": "encryption_at_rest", "severity": "critical", "field": "data_sensitivity" }`}</p>
            </div>
          </div>

          {/* Step 2: Prescriptive Remediation */}
          <div className="bg-zinc-800/30 rounded-lg p-4 border border-yellow-500/20">
            <h4 className="text-yellow-400 font-semibold mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded bg-yellow-500/20 flex items-center justify-center text-xs font-bold">2</span>
              Prescriptive Remediation Generation
            </h4>
            <p className="text-zinc-400 text-sm mb-3">
              For each detected issue, the engine generates a specific remediation instruction with exact steps.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-zinc-900/50 p-3 rounded">
                <p className="text-white text-sm font-medium mb-2">Remediation Object Structure</p>
                <ul className="text-zinc-500 text-xs space-y-1">
                  <li>• <code className="text-cyan-400">id</code> - Unique identifier</li>
                  <li>• <code className="text-cyan-400">severity</code> - critical/high/medium</li>
                  <li>• <code className="text-cyan-400">tab</code> - Which form tab to fix</li>
                  <li>• <code className="text-cyan-400">field</code> - Exact field name</li>
                  <li>• <code className="text-cyan-400">current_value</code> - What's set now</li>
                  <li>• <code className="text-cyan-400">action</code> - What to change</li>
                  <li>• <code className="text-cyan-400">exact_steps</code> - Step-by-step fix</li>
                </ul>
              </div>
              <div className="bg-zinc-900/50 p-3 rounded">
                <p className="text-white text-sm font-medium mb-2">Example: Encryption Not Enforced</p>
                <div className="text-xs text-zinc-400 space-y-1">
                  <p><span className="text-red-400">Issue:</span> Data Sensitivity = "Internal"</p>
                  <p><span className="text-green-400">Fix:</span> Change to "Confidential"</p>
                  <p><span className="text-cyan-400">Tab:</span> Security</p>
                  <p><span className="text-cyan-400">Field:</span> data_sensitivity</p>
                  <p className="mt-2 text-yellow-400">→ "Fix Now" button navigates directly</p>
                </div>
              </div>
            </div>
          </div>

          {/* Step 3: Architecture Generation with Guardrails */}
          <div className="bg-zinc-800/30 rounded-lg p-4 border border-purple-500/20">
            <h4 className="text-purple-400 font-semibold mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded bg-purple-500/20 flex items-center justify-center text-xs font-bold">3</span>
              Architecture Generation with Security Guardrails
            </h4>
            <p className="text-zinc-400 text-sm mb-3">
              When generating architecture (Phase 3), the engine enforces security patterns based on compliance requirements.
            </p>
            <div className="bg-zinc-900/50 rounded p-4">
              <p className="text-white text-sm mb-3">Automatic Enforcements:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-white">If Data Sensitivity ≥ Confidential</p>
                    <p className="text-zinc-500">→ Adds encryption at rest to all storage</p>
                    <p className="text-zinc-500">→ Enforces private VPC networking</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-white">If SLA ≥ 99.9%</p>
                    <p className="text-zinc-500">→ Deploys across multiple AZs</p>
                    <p className="text-zinc-500">→ Adds auto-scaling policies</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-white">If Auth includes MFA + SSO</p>
                    <p className="text-zinc-500">→ Configures IAM least privilege</p>
                    <p className="text-zinc-500">→ Adds identity federation</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-white">If HIPAA/PCI-DSS selected</p>
                    <p className="text-zinc-500">→ Adds audit logging to CloudTrail</p>
                    <p className="text-zinc-500">→ Enables compliance monitoring</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Step 4: IaC Generation with Scanner Integration */}
          <div className="bg-zinc-800/30 rounded-lg p-4 border border-green-500/20">
            <h4 className="text-green-400 font-semibold mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded bg-green-500/20 flex items-center justify-center text-xs font-bold">4</span>
              IaC Generation + Real Scanner Validation
            </h4>
            <p className="text-zinc-400 text-sm mb-3">
              Generated IaC is validated by real security tools before export. Any issues are auto-fixed.
            </p>
            <div className="bg-zinc-900/50 rounded p-4 font-mono text-xs overflow-x-auto">
              <p className="text-zinc-500"># IaC generation pipeline:</p>
              <p className="text-cyan-400 mt-2">terraform_code = generate_terraform(architecture)</p>
              <p className="text-purple-400 mt-1">checkov_results = run_checkov(terraform_code)</p>
              <p className="text-purple-400">tfsec_results = run_tfsec(terraform_code)</p>
              <p className="text-purple-400">trivy_results = run_trivy(terraform_code)</p>
              <p className="text-purple-400">gitleaks_results = run_gitleaks(terraform_code)</p>
              <p className="text-yellow-400 mt-2"># If issues found:</p>
              <p className="text-green-400">for issue in all_issues:</p>
              <p className="text-green-400 ml-4">  terraform_code = apply_auto_fix(terraform_code, issue)</p>
              <p className="text-cyan-400 mt-2">return clean_terraform_code  # Zero hardcoded secrets</p>
            </div>
          </div>

          {/* Auto-Fix Rules Table */}
          <div className="bg-zinc-800/30 rounded-lg p-4 border border-zinc-700/50">
            <h4 className="text-white font-semibold mb-3">Auto-Fix Rules Applied</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-700">
                    <th className="text-left py-2 text-zinc-400 font-medium">Scanner Finding</th>
                    <th className="text-left py-2 text-zinc-400 font-medium">Auto-Fix Applied</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800 text-xs">
                  <tr>
                    <td className="py-2 text-red-400">Hardcoded AWS credentials</td>
                    <td className="py-2 text-green-400">Replace with AWS Secrets Manager reference</td>
                  </tr>
                  <tr>
                    <td className="py-2 text-red-400">S3 bucket without encryption</td>
                    <td className="py-2 text-green-400">Add server_side_encryption_configuration block</td>
                  </tr>
                  <tr>
                    <td className="py-2 text-red-400">Security group allows 0.0.0.0/0</td>
                    <td className="py-2 text-green-400">Restrict to VPC CIDR or specific IPs</td>
                  </tr>
                  <tr>
                    <td className="py-2 text-red-400">RDS without backup retention</td>
                    <td className="py-2 text-green-400">Add backup_retention_period = 7 (based on RPO)</td>
                  </tr>
                  <tr>
                    <td className="py-2 text-red-400">CloudTrail not enabled</td>
                    <td className="py-2 text-green-400">Add aws_cloudtrail resource for audit logging</td>
                  </tr>
                  <tr>
                    <td className="py-2 text-red-400">IAM policy allows *</td>
                    <td className="py-2 text-green-400">Scope down to specific resource ARNs</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Why This Matters */}
          <div className="bg-gradient-to-r from-green-500/10 to-cyan-500/10 rounded-lg p-4 border border-green-500/30">
            <h4 className="text-white font-semibold mb-3">Why This Matters: Manual vs. ZeroZone</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-zinc-900/50 p-3 rounded">
                <p className="text-red-400 text-sm font-medium mb-2">Manual Process</p>
                <ul className="text-zinc-500 text-xs space-y-1">
                  <li>• Write IaC manually</li>
                  <li>• Run scanners after deployment</li>
                  <li>• Find 50+ issues</li>
                  <li>• Fix issues one by one</li>
                  <li>• Re-run scanners</li>
                  <li>• Repeat 3-5 times</li>
                  <li className="text-red-400">→ 2-3 weeks to production-ready</li>
                </ul>
              </div>
              <div className="bg-zinc-900/50 p-3 rounded">
                <p className="text-green-400 text-sm font-medium mb-2">ZeroZone Self-Healing</p>
                <ul className="text-zinc-500 text-xs space-y-1">
                  <li>• Fill intake form (5 min)</li>
                  <li>• Auto-audit runs pre-generation</li>
                  <li>• Prescriptive fixes shown</li>
                  <li>• One-click fix navigation</li>
                  <li>• IaC generated with fixes built-in</li>
                  <li>• Scanners validate final code</li>
                  <li className="text-green-400">→ 15 minutes to certified IaC</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  // Filter sections based on search
  const filteredSections = guideSections.filter(section => 
    section.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    section.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="border-b border-white/5 bg-[#09090b]/80 backdrop-blur-lg sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                className="text-zinc-400 hover:text-white"
                onClick={() => navigate('/')}
                data-testid="back-home-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Home
              </Button>
              <div className="w-px h-6 bg-white/10" />
              <div className="flex items-center gap-3 group">
                <div className="relative">
                  <div className="absolute inset-0 bg-[#00f0ff]/20 blur-xl rounded-full scale-75 group-hover:scale-100 transition-transform" />
                  <img src="/zerozone-logo.png" alt="ZeroZone Logo" className="relative w-28 h-28 object-contain drop-shadow-[0_0_18px_rgba(59,130,246,0.5)]" />
                </div>
                <span className="font-semibold text-white">User Guide</span>
              </div>
            </div>
            
            <Button
              className="bg-[#00f0ff] text-black font-semibold hover:bg-[#00f0ff]/90 rounded-sm"
              onClick={downloadGuide}
              data-testid="download-guide-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Guide
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-white/5 bg-gradient-to-b from-[#00f0ff]/5 to-transparent">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-10 md:py-16">
          <div className="text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              ZeroZone User Guide
            </h1>
            <p className="text-zinc-400 max-w-2xl mx-auto mb-8">
              A comprehensive guide to creating audit-ready cloud architectures. Learn how to use every feature, from project intake to certified IaC export.
            </p>
            
            {/* Search */}
            <div className="max-w-md mx-auto relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                type="text"
                placeholder="Search the guide..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-zinc-900/50 border-zinc-700 text-white"
                data-testid="guide-search-input"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 md:px-6 py-8">
        <div className="space-y-4">
          {filteredSections.map((section) => (
            <div 
              key={section.id}
              className="bg-zinc-900/40 border border-zinc-800 rounded-lg overflow-hidden"
            >
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-zinc-800/30 transition-colors"
                data-testid={`section-${section.id}-toggle`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#00f0ff]/10 border border-[#00f0ff]/30 flex items-center justify-center text-[#00f0ff]">
                    {section.icon}
                  </div>
                  <div className="text-left">
                    <h3 className="text-white font-semibold">{section.title}</h3>
                    <p className="text-zinc-500 text-sm">{section.description}</p>
                  </div>
                </div>
                {expandedSections[section.id] ? (
                  <ChevronDown className="w-5 h-5 text-zinc-400" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-zinc-400" />
                )}
              </button>
              
              {expandedSections[section.id] && (
                <div className="px-6 pb-6 pt-2 border-t border-zinc-800">
                  {section.content}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Quick Links */}
        <div className="mt-12 bg-zinc-900/40 border border-zinc-800 rounded-lg p-6">
          <h3 className="text-white font-semibold mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Button
              variant="outline"
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 justify-start"
              onClick={() => navigate('/wizard')}
            >
              <Zap className="w-4 h-4 mr-2" />
              New Project
            </Button>
            <Button
              variant="outline"
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 justify-start"
              onClick={() => navigate('/scan')}
            >
              <Scan className="w-4 h-4 mr-2" />
              Bring Your Code
            </Button>
            <Button
              variant="outline"
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 justify-start"
              onClick={() => navigate('/projects')}
            >
              <Layers className="w-4 h-4 mr-2" />
              My Projects
            </Button>
            <Button
              variant="outline"
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 justify-start"
              onClick={downloadGuide}
              data-testid="quick-action-download-guide-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Download Guide
            </Button>
          </div>
        </div>

      </main>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/5">
        <div className="max-w-5xl mx-auto text-center text-zinc-600 text-sm">
          ZeroZone Global Resilience Cloud Architect - User Guide v1.0
        </div>
      </footer>
    </div>
  );
};
